import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

// 리메이크 원 건 연결 창 (2026-10-24 거래명세서 2단계 ④ — 명세 §2, 계약 §1·§2·§5)
//
// 작성 화면의 리메이크 줄에서 [원 건 보기]를 누르면 열린다. "원래 만든 것"과 "다시 만든 것"을 나란히 보여주고,
// 기공소가 책임(기공소 / 치과 요청·변경 / 같이 / 모름)을 고르면 단가 '제안'을 보여준다.
//   - 제안은 버튼을 눌러야 단가에 들어간다(자동 적용 금지). 결정은 사람.
//   - 책임 구분(remakeLiability)은 기공소 내부 기록 — 인쇄물·치과 화면·공유 스냅샷에 나가지 않는다.
//   - 덴탈클러스터는 책임을 판정하지 않는다(사유로 책임을 미리 채우지 않음, RemakeAnalysis 원칙과 같음).
//
// 원 건 찾기: 부모(CreateTransactionStatement)가 이미 읽은 자료(이 명세서 줄 + 최근 6개월 명세서 줄)에서 먼저 찾아 origin으로 넘긴다.
//   못 찾았을 때만 이 창이 문서 1~2건을 읽는다(옛 수집분의 parsedCases.remakeOfId, 원 건 parsedCases/workOrders의 날짜·치식).
//
// props
//   item            리메이크 줄(작성 화면 items 원소)
//   origin          부모가 찾은 원 건 | null — { linkedBy:'id'|'guess', where:'current'|'statement', number, status, date(ms), unitPrice, toothInfo }
//   statementNumber 지금 명세서 번호(없으면 '')
//   findOriginById  (ids[]) => origin|null — 늦게 알아낸 id로 부모 자료를 다시 찾는다
//   onPatch         (patch) => void — 줄에 필드 기록 (remakeLiability · remakeOf · remakeUnlinked · remakeOfCaseId)
//                   remakeLiability는 화면 값 — 저장은 부모가 statementRemakeNotes/{statementId}에 따로 한다(명세서 문서엔 안 들어감, 검수 P2-7)
//   onApplyPrice    (price) => void — 사람이 누른 제안 단가 적용
//   onClose         () => void

// 법무 고정 문구 (계약 §2 — 그대로)
const LEGAL_NOTE = '책임 구분은 기공소 내부 기록이며 치과에 표시되지 않습니다. 덴탈클러스터는 책임을 판정하지 않으며, 무상·유상은 기공소가 정합니다. 분쟁 시 이 기록도 자료로 쓰일 수 있습니다.';

const LIABILITY_OPTIONS = [
    { code: 'lab', label: '기공소' },
    { code: 'clinic', label: '치과 요청·변경' },
    { code: 'shared', label: '같이' },
    { code: 'unknown', label: '모름' },
];

const pad2 = (n) => String(n).padStart(2, '0');
const toYmd = (ms) => {
    if (!ms) return '';
    const d = new Date(ms);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const toMd = (ms) => {
    if (!ms) return '-';
    const d = new Date(ms);
    if (isNaN(d.getTime())) return '-';
    return `${d.getMonth() + 1}/${d.getDate()}`;
};
const tsToMs = (v) => {
    if (!v) return 0;
    if (typeof v === 'string') { const t = Date.parse(v); return isNaN(t) ? 0 : t; }
    if (v.seconds !== undefined) return v.seconds * 1000;
    if (typeof v.toDate === 'function') return v.toDate().getTime();
    if (v instanceof Date) return v.getTime();
    const t = new Date(v).getTime();
    return isNaN(t) ? 0 : t;
};
const won = (n) => `${(Number(n) || 0).toLocaleString()}원`;
const statusLabel = (s) => (s === 'confirmed' ? '발행됨' : s === 'draft' ? '작성 중' : '');

// 원 건 문서(parsedCases / workOrders)에서 치식·품목 요약 — 환자 이름은 쓰지 않는다
const toothSummaryOfCase = (c) => {
    const teeth = (c.teethDetails && c.teethDetails.length) ? c.teethDetails.map(d => d.tooth) : (c.teeth || []);
    const kind = (c.teethDetails && c.teethDetails[0] && c.teethDetails[0].prosthesis) || (c.indications || [])[0] || '';
    const t = teeth.filter(Boolean);
    return `${t.length ? `#${t.join(',')}` : '#-'}${kind ? ` ${kind}` : ''}`;
};
const toothSummaryOfOrder = (o) => {
    const list = Array.isArray(o.items) ? o.items : [];
    const teeth = list.map(it => it.toothNumber).filter(Boolean);
    const kind = (list.find(it => it.prosthesis) || {}).prosthesis || '';
    return `${teeth.length ? `#${teeth.join(',')}` : '#-'}${kind ? ` ${kind}` : ''}`;
};

function RemakeLinkModal({ item, origin, statementNumber, findOriginById, onPatch, onApplyPrice, onClose }) {
    // 부모 자료에서 못 찾았을 때 이 창이 직접 찾은 결과
    const [lateOrigin, setLateOrigin] = useState(null);
    const [lookup, setLookup] = useState(origin ? 'done' : 'loading');   // 'loading' | 'done' | 'error'

    useEffect(() => {
        if (origin) { setLookup('done'); return undefined; }
        let alive = true;
        (async () => {
            try {
                let caseId = item.remakeOfCaseId ? String(item.remakeOfCaseId) : '';
                const orderId = item.remakeOfOrderId ? String(item.remakeOfOrderId) : '';
                // 옛 수집분: 복사 의뢰서에 원 건 id가 없던 시절 — 이 줄의 parsedCases 문서에서 remakeOfId를 읽는다 (1건)
                if (!caseId && !orderId) {
                    const raw = String(item.parsedCaseId || '') || (String(item.orderId || '').startsWith('case:') ? String(item.orderId) : '');
                    const pcId = raw.replace(/^case:/, '');
                    if (pcId) {
                        const pc = await getDoc(doc(db, 'parsedCases', pcId));
                        const rid = pc.exists() ? (pc.data().remakeOfId || '') : '';
                        if (rid) {
                            caseId = String(rid);
                            if (alive) onPatch({ remakeOfCaseId: caseId });   // 다음부터는 바로 찾도록 줄에 남긴다
                        }
                    }
                }
                if (!caseId && !orderId) { if (alive) setLookup('done'); return; }
                // 늦게 알아낸 id로 부모 자료(명세서 줄) 다시 찾기 — 금액·번호가 있는 쪽이 우선
                const again = findOriginById([caseId, orderId]);
                if (again) { if (alive) { setLateOrigin(again); setLookup('done'); } return; }
                // 명세서에 없는 원 건(아직 청구 전·6개월 넘음) — 원 건 문서에서 날짜·치식만 (1건)
                let found = null;
                if (caseId) {
                    const cs = await getDoc(doc(db, 'parsedCases', caseId));
                    if (cs.exists()) {
                        const c = cs.data();
                        found = { linkedBy: 'id', where: 'case', number: '', status: '', date: tsToMs(c.fileMtime) || tsToMs(c.createdAt), unitPrice: 0, toothInfo: toothSummaryOfCase(c) };
                    }
                }
                if (!found && orderId) {
                    const os = await getDoc(doc(db, 'workOrders', orderId));
                    if (os.exists()) {
                        const o = os.data();
                        found = { linkedBy: 'id', where: 'order', number: '', status: '', date: tsToMs(o.createdAt), unitPrice: 0, toothInfo: toothSummaryOfOrder(o) };
                    }
                }
                if (!found && item._remakeOfDateHint) {
                    found = { linkedBy: 'id', where: 'order', number: '', status: '', date: item._remakeOfDateHint, unitPrice: 0, toothInfo: '' };
                }
                if (alive) { setLateOrigin(found); setLookup('done'); }
            } catch (e) {
                // 조용한 실패 금지 — id·코드만 남긴다(환자 정보 X). 화면은 '못 찾음'으로 계속.
                console.warn('[리메이크 연결] 원 건 조회 실패:', e?.code || e?.message || e);
                if (alive) setLookup('error');
            }
        })();
        return () => { alive = false; };
        // 창을 연 줄 기준 1회만 (onPatch로 줄이 바뀌어도 다시 읽지 않는다)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.id]);

    const o = origin || lateOrigin;
    const savedRemakeOf = !item.remakeUnlinked && item.remakeOf && item.remakeOf.date ? item.remakeOf : null;

    // id로 확실히 찾은 원 건은 표시값(날짜·번호)을 줄에 고정 — 늦게 찾은 경우만(부모 자료로 찾은 건 저장 때 자동으로 채워진다)
    useEffect(() => {
        if (!lateOrigin || lateOrigin.linkedBy !== 'id' || savedRemakeOf || item.remakeUnlinked) return;   // 사람이 끊은 연결은 되살리지 않는다 (P2-8)
        const date = toYmd(lateOrigin.date);
        if (date) onPatch({ remakeOf: { date, ...(lateOrigin.number ? { statementNumber: lateOrigin.number } : {}) } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lateOrigin]);

    const liability = item.remakeLiability || '';
    const curPrice = Number(item.unitPrice) || 0;
    const tablePrice = Number(item.suggestedUnitPrice) || 0;
    const originPrice = o ? (Number(o.unitPrice) || 0) : 0;

    const linkGuess = () => {
        const date = toYmd(o && o.date);
        if (!date) return;
        onPatch({ remakeOf: { date, ...(o.number ? { statementNumber: o.number } : {}) }, remakeUnlinked: false });
    };
    // [연결 끊기] — 저장 때 원 건 표기를 자동으로 다시 채우지 않도록 플래그를 남긴다 (검수 P2-8). 다시 연결하면 플래그 제거.
    const unlink = () => onPatch({ remakeOf: null, remakeUnlinked: true });
    // 지금 이 줄에 원 건 표기가 붙는가: 끊지 않았고, 저장된 표기가 있거나 id로 확실히 찾은 원 건
    const isLinked = !item.remakeUnlinked && (!!savedRemakeOf || (!!o && o.linkedBy === 'id' && !!toYmd(o.date)));

    // 제안 (계약 §1): 기공소→0원, 치과 요청·변경→단가표 제안가(없으면 원 건 단가), 같이→제안 없음+[원 건 절반], 모름→그대로
    const renderSuggestion = () => {
        if (!liability) return <div style={styles.hint}>{'책임을 고르면 단가 제안이 나옵니다. 제안은 버튼을 눌러야 들어갑니다.'}</div>;
        if (liability === 'lab') {
            return (
                <div style={styles.suggestRow}>
                    <span>{'▶ 제안: 기공소 책임 — 보통 무상입니다'}</span>
                    <button type="button" style={styles.applyBtn} onClick={() => onApplyPrice(0)}>{'무상으로 (0원 적용)'}</button>
                </div>
            );
        }
        if (liability === 'clinic') {
            const p = tablePrice || originPrice;
            const src = tablePrice ? '단가표' : '처음 받은 금액';
            return p > 0 ? (
                <div style={styles.suggestRow}>
                    <span>{`▶ 제안: 치과 요청·변경 — 유상 제안 ${won(p)} (${src})`}</span>
                    <button type="button" style={styles.applyBtn} onClick={() => onApplyPrice(p)}>{`유상으로 (${won(p)} 적용)`}</button>
                </div>
            ) : (
                <div style={styles.suggestRow}>{'▶ 치과 요청·변경 — 단가표에 맞는 값이 없습니다. 표의 단가 칸에 금액을 직접 넣으세요.'}</div>
            );
        }
        if (liability === 'shared') {
            const half = Math.round(originPrice / 2);
            return (
                <div style={styles.suggestRow}>
                    <span>{'▶ 같이 책임 — 금액은 협의해서 넣으세요'}</span>
                    {half > 0 && (
                        <button type="button" style={styles.applyBtn} onClick={() => onApplyPrice(half)}>{`처음 금액의 절반 ${won(half)} 적용`}</button>
                    )}
                </div>
            );
        }
        return <div style={styles.suggestRow}>{"▶ 모름 — '무상 여부 확인' 그대로 둡니다. 정해지면 다시 고르세요."}</div>;
    };

    const linkLabel = !o ? '' : o.linkedBy === 'guess'
        ? '추정 — 같은 환자·치식 (확인 후 연결)'
        : (o.where === 'order' || item.remakeOfOrderId) && !item.remakeOfCaseId ? '연결: 의뢰서 기록(확실)' : '연결: 파일 기록(확실)';
    const originWhere = !o ? '' : o.where === 'current'
        ? `이번 명세서${o.number ? ` · ${o.number}` : ''}`
        : o.where === 'statement'
            ? `${o.number || '번호 없음'}${statusLabel(o.status) ? ` · ${statusLabel(o.status)}` : ''}`
            : '아직 명세서에 없음';

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <div style={styles.header}>
                    <span style={{ fontWeight: 800, fontSize: 16 }}>{item.remakeKind === 'repair' ? '수리 연결' : '리메이크 연결'}</span>
                    <button type="button" onClick={onClose} style={styles.closeBtn} aria-label="닫기">{'×'}</button>
                </div>

                <div style={styles.cols}>
                    <div style={styles.col}>
                        <div style={styles.colTitle}>{'처음 만든 것'}</div>
                        {lookup === 'loading' ? (
                            <div style={styles.muted}>{'처음 작업을 찾는 중…'}</div>
                        ) : o ? (
                            <>
                                <div>{`${toMd(o.date)} 작업`}</div>
                                <div>{originWhere}</div>
                                <div>{`${o.toothInfo || '-'} · ${originPrice > 0 ? won(originPrice) : '금액 기록 없음'}`}</div>
                                <div style={{ ...styles.muted, marginTop: 4, color: o.linkedBy === 'guess' ? '#B45309' : '#047857' }}>{linkLabel}</div>
                                {isLinked ? (
                                    <div style={{ marginTop: 6, fontSize: 12 }}>
                                        <span style={{ color: '#047857' }}>{'연결됨 — 명세서에 처음 작업 날짜·번호가 표시됩니다'}</span>
                                        <button type="button" onClick={unlink} style={styles.linkBtn}>{'연결 끊기'}</button>
                                    </div>
                                ) : toYmd(o.date) ? (
                                    <div style={{ marginTop: 6 }}>
                                        {item.remakeUnlinked && <div style={styles.muted}>{'연결을 끊었습니다 — 명세서에 처음 작업 표시 없음'}</div>}
                                        <button type="button" onClick={linkGuess} style={{ ...styles.applyBtn, marginTop: 4 }}>{item.remakeUnlinked ? '다시 연결' : '이 건으로 연결'}</button>
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <>
                                <div style={{ color: '#B45309', fontWeight: 600 }}>{'처음 만든 것을 찾지 못했습니다'}</div>
                                <div style={styles.muted}>{lookup === 'error' ? '처음 작업 기록을 읽지 못했습니다. 책임 선택은 할 수 있습니다.' : '(6개월이 넘었거나 종이로 청구한 건일 수 있습니다)'}</div>
                                {savedRemakeOf && (
                                    <div style={{ marginTop: 6 }}>{`저장된 연결: 처음 작업 ${savedRemakeOf.date}${savedRemakeOf.statementNumber ? ` · ${savedRemakeOf.statementNumber}` : ''}`}</div>
                                )}
                            </>
                        )}
                    </div>
                    <div style={styles.col}>
                        <div style={styles.colTitle}>{'다시 만든 것'}</div>
                        <div>{`${toMd(item._sortTimestamp || tsToMs(item.createdAt))} 작업${item.source === 'auto' ? ' (수집분)' : ''}`}</div>
                        <div>{`이번 명세서${statementNumber ? ` · ${statementNumber}` : ''}`}</div>
                        <div>{`${item.toothInfo || '-'} · ${won(curPrice)}`}</div>
                    </div>
                </div>

                <div style={styles.section}>
                    <div style={styles.label}>{'책임'}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {LIABILITY_OPTIONS.map(opt => (
                            <button key={opt.code} type="button"
                                onClick={() => onPatch({ remakeLiability: opt.code })}
                                style={liability === opt.code ? styles.chipOn : styles.chip}>
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <div style={{ marginTop: 10 }}>{renderSuggestion()}</div>
                </div>

                <div style={styles.legal}>{LEGAL_NOTE}</div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                    <button type="button" onClick={onClose} style={styles.doneBtn}>{'닫기'}</button>
                </div>
            </div>
        </div>
    );
}

const styles = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
    modal: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', padding: 20, boxShadow: '0 20px 40px rgba(0,0,0,0.2)', fontSize: 13, color: '#0F172A' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
    closeBtn: { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748B', lineHeight: 1 },
    cols: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 },
    col: { border: '1px solid #E2E8F0', borderRadius: 8, padding: 12, background: '#F8FAFC', lineHeight: 1.6 },
    colTitle: { fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 4 },
    muted: { fontSize: 11.5, color: '#64748B' },
    section: { marginTop: 14, borderTop: '1px solid #E2E8F0', paddingTop: 12 },
    label: { fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 6 },
    chip: { fontSize: 12.5, padding: '5px 12px', borderRadius: 999, border: '1px solid #CBD5E1', background: '#fff', color: '#334155', cursor: 'pointer' },
    chipOn: { fontSize: 12.5, padding: '5px 12px', borderRadius: 999, border: '1px solid #4338CA', background: '#EEF2FF', color: '#3730A3', fontWeight: 700, cursor: 'pointer' },
    hint: { fontSize: 12, color: '#64748B' },
    suggestRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 13, color: '#1E293B' },
    applyBtn: { fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: '1px solid #4338CA', background: '#4338CA', color: '#fff', cursor: 'pointer' },
    linkBtn: { marginLeft: 8, fontSize: 11.5, color: '#64748B', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', padding: 0 },
    legal: { marginTop: 14, fontSize: 11.5, color: '#475569', background: '#F1F5F9', borderRadius: 8, padding: '8px 10px', lineHeight: 1.5 },
    doneBtn: { fontSize: 13, fontWeight: 700, padding: '6px 16px', borderRadius: 8, border: '1px solid #CBD5E1', background: '#fff', color: '#334155', cursor: 'pointer' },
};

export default RemakeLinkModal;
